import { useState } from 'react';
import {
  Landmark,
  Shield,
  CreditCard,
  Share2,
  TrendingUp,
  Coins,
  Gift,
  Bitcoin,
  AlertCircle,
  Settings,
  ChevronRight,
} from 'lucide-react';
import {
  TenantModuleStatus,
  PlanTier,
  TIER_LABELS,
  MODULE_CATEGORY_COLORS,
} from '../../types/modules';

// Icon mapping
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Landmark,
  Shield,
  CreditCard,
  Share2,
  TrendingUp,
  Coins,
  Gift,
  Bitcoin,
};

interface ModuleCardProps {
  module: TenantModuleStatus;
  tenantTier: PlanTier;
  onToggle: (moduleId: string, enabled: boolean) => void;
  onConfigure: (module: TenantModuleStatus) => void;
  isLoading?: boolean;
  canManage?: boolean;
}

export function ModuleCard({
  module,
  tenantTier,
  onToggle,
  onConfigure,
  isLoading = false,
  canManage = true,
}: ModuleCardProps) {
  const [isToggling, setIsToggling] = useState(false);

  const IconComponent = ICON_MAP[module.icon] || Landmark;
  const categoryColor = MODULE_CATEGORY_COLORS[module.category] || 'gray';
  
  // Check if module is available in tenant's tier
  const isAvailableInTier = module.availableInTiers.includes(tenantTier);
  const minRequiredTier = module.availableInTiers[0];
  
  // Check if has missing dependencies (dependencies that are not enabled)
  const hasDependencies = module.dependencies.length > 0;
  
  // Check if dependencies are satisfied (for enabling - not needed for disabling)
  // Note: This requires the parent to pass enabled modules info
  // For now, we'll show the warning but allow the toggle (backend will validate)
  const hasMissingDependencies = hasDependencies && !module.isEnabled;
  
  // Can toggle: available in tier AND (is enabled OR no missing dependencies)
  const canToggle = isAvailableInTier && canManage;

  const handleToggle = async () => {
    if (!canToggle || isLoading) return;
    
    setIsToggling(true);
    try {
      await onToggle(module.id, !module.isEnabled);
    } finally {
      setIsToggling(false);
    }
  };

  const getTierBadgeColor = (tier: PlanTier) => {
    switch (tier) {
      case PlanTier.BASIC:
        return 'bg-gray-100 text-gray-700';
      case PlanTier.STANDARD:
        return 'bg-blue-100 text-blue-700';
      case PlanTier.PREMIUM:
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryBadgeColor = () => {
    const colors: Record<string, string> = {
      Banking: 'bg-blue-50 text-blue-600',
      Security: 'bg-red-50 text-red-600',
      Credit: 'bg-green-50 text-green-600',
      Integration: 'bg-purple-50 text-purple-600',
      Cards: 'bg-orange-50 text-orange-600',
      Investments: 'bg-cyan-50 text-cyan-600',
      Blockchain: 'bg-yellow-50 text-yellow-600',
      Benefits: 'bg-pink-50 text-pink-600',
      Crypto: 'bg-amber-50 text-amber-600',
    };
    return colors[module.category] || 'bg-gray-50 text-gray-600';
  };

  return (
    <div
      className={`
        relative rounded-xl border p-5 transition-all duration-200
        ${module.isEnabled 
          ? 'border-green-200 bg-green-50/30 shadow-sm' 
          : 'border-gray-200 bg-white hover:border-gray-300'
        }
        ${!isAvailableInTier ? 'opacity-60' : ''}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={`
              p-2.5 rounded-lg
              ${module.isEnabled 
                ? 'bg-green-100 text-green-600' 
                : 'bg-gray-100 text-gray-500'
              }
            `}
          >
            <IconComponent className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{module.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryBadgeColor()}`}>
              {module.category}
            </span>
          </div>
        </div>

        {/* Toggle Switch */}
        {canManage && (
          <button
            onClick={handleToggle}
            disabled={!canToggle || isLoading || isToggling}
            title={!isAvailableInTier 
              ? `Requer plano ${TIER_LABELS[minRequiredTier]} ou superior`
              : hasMissingDependencies 
                ? `Ative primeiro: ${module.dependencies.join(', ')}`
                : module.isEnabled ? 'Clique para desativar' : 'Clique para ativar'
            }
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors
              ${module.isEnabled ? 'bg-green-500' : 'bg-gray-300'}
              ${(!canToggle || isLoading || isToggling) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${module.isEnabled ? 'translate-x-6' : 'translate-x-1'}
              `}
            />
          </button>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {module.description}
      </p>

      {/* Tier Badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500">Disponível em:</span>
        <div className="flex gap-1">
          {module.availableInTiers.map((tier) => (
            <span
              key={tier}
              className={`text-xs px-2 py-0.5 rounded-full ${getTierBadgeColor(tier)}`}
            >
              {TIER_LABELS[tier]}
            </span>
          ))}
        </div>
      </div>

      {/* Dependencies Warning */}
      {hasDependencies && !module.isEnabled && (
        <div className="flex items-center gap-2 text-xs text-amber-600 mb-3 bg-amber-50 p-2 rounded-lg">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            <strong>Ative primeiro:</strong> {module.dependencies.join(', ')}
          </span>
        </div>
      )}

      {/* Tier Restriction Warning */}
      {!isAvailableInTier && (
        <div className="flex items-center gap-2 text-xs text-red-600 mb-3">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>Requer plano {TIER_LABELS[minRequiredTier]} ou superior</span>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {module.isEnabled && module.activatedAt && (
            <span className="text-xs text-gray-500">
              Ativo desde {new Date(module.activatedAt).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>

        {module.isEnabled && canManage && (
          <button
            onClick={() => onConfigure(module)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <Settings className="w-4 h-4" />
            Configurar
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default ModuleCard;
